import * as React from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Globe, ServerCog, ExternalLink, Loader2, RefreshCw } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { api, ApiError } from "@/lib/api";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AllocationEditor } from "@/components/containers/AllocationEditor";
import { DnsTab } from "@/components/containers/DnsTab";
import { HostingPanel } from "@/components/containers/HostingPanel";
import { MailboxList } from "@/components/containers/MailboxList";
import { SslTab } from "@/components/containers/SslTab";
import { useContainer } from "@/hooks/useContainers";
import { cn, formatDate } from "@/lib/utils";
import type { ContainerDetailDto } from "@/types/api";

export function DomainDetailPage() {
  const { id } = useParams<{ id: string }>();
  const numericId = id ? Number(id) : undefined;
  const { data, isLoading, error } = useContainer(numericId);

  if (isLoading) return <DetailSkeleton />;
  if (error || !data) return <NotFoundState />;

  return (
    <div className="space-y-5 sm:space-y-6">
      <BackLink />

      <DomainHeader container={data} />

      <TransferBanner container={data} />
      <ConnectDomainPanel container={data} />

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList className="w-full sm:w-auto">
          <TabsTrigger value="overview" className="flex-1 sm:flex-none">Overview</TabsTrigger>
          <TabsTrigger value="mailboxes" className="flex-1 sm:flex-none">Mailboxes</TabsTrigger>
          <TabsTrigger value="dns" className="flex-1 sm:flex-none">DNS</TabsTrigger>
          <TabsTrigger value="ssl" className="flex-1 sm:flex-none">SSL</TabsTrigger>
          <TabsTrigger value="allocation" className="flex-1 sm:flex-none">Allocation</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-5">
          {/* Live hosting panel once the 20i package is stamped; until then a
              quiet setup notice — provisioning is automatic after checkout. */}
          {data.twentyi_package_id ? (
            <HostingPanel containerId={data.id} twentyiPackageId={data.twentyi_package_id} />
          ) : (
            <Card>
              <CardContent className="flex items-center gap-3 py-4 text-sm text-muted-foreground">
                <ServerCog className="h-4 w-4 shrink-0" />
                Hosting for {data.domain_name} is being set up automatically — mailboxes,
                DNS and SSL unlock here in a few minutes.
              </CardContent>
            </Card>
          )}

          <UsageGrid container={data} />
          <ServicesList container={data} />
        </TabsContent>

        <TabsContent value="mailboxes">
          <MailboxList
            containerId={data.id}
            domainName={data.domain_name}
            hasPackage={Boolean(data.twentyi_package_id)}
            allocated={data.allocation?.mailboxes_allocated ?? 0}
          />
        </TabsContent>

        <TabsContent value="dns">
          <DnsTab
            containerId={data.id}
            domainName={data.domain_name}
            hasPackage={Boolean(data.twentyi_package_id)}
          />
        </TabsContent>

        <TabsContent value="ssl">
          <SslTab
            containerId={data.id}
            domainName={data.domain_name}
            hasPackage={Boolean(data.twentyi_package_id)}
          />
        </TabsContent>

        <TabsContent value="allocation">
          {data.allocation ? (
            <AllocationEditor containerId={data.id} initial={data.allocation} />
          ) : (
            <Card>
              <CardContent className="py-8 text-center text-sm text-muted-foreground">
                This container has no allocation row yet — odd, normally it's auto-created.
                Try reloading.
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ── Subcomponents ────────────────────────────────────────────
interface ConnectInfo {
  nameservers: string[];
  email_records: { type: string; host: string; value: string; priority?: number | null; note: string }[];
  dkim_note: string;
}

/** Shown while an existing (customer-owned) domain hasn't been pointed at our
 *  nameservers yet. Primary path: change nameservers (we then manage the whole
 *  zone automatically). Advanced: individual mail records for keep-your-DNS. */
function ConnectDomainPanel({ container }: { container: ContainerDetailDto }) {
  const pending = (container.nameserver_status ?? "") === "pending_customer";
  const { toast } = useToast();
  const [info, setInfo] = React.useState<ConnectInfo | null>(null);
  const [showAdvanced, setShowAdvanced] = React.useState(false);

  React.useEffect(() => {
    if (!pending) return;
    api.get<ConnectInfo>("/api/domains/connect-info")
      .then(setInfo)
      .catch(() => setInfo(null));
  }, [pending]);

  if (!pending) return null;

  function copy(value: string) {
    navigator.clipboard?.writeText(value);
    toast({ title: "Copied", description: value });
  }

  return (
    <Card className="border-amber-300/60">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Globe className="h-4 w-4 text-amber-500" />
          Connect {container.domain_name}
        </CardTitle>
        <CardDescription>
          Your domain stays at your current registrar — just point its nameservers at
          HosterDash. We'll detect the change automatically (checks run every few minutes;
          DNS updates can take up to 24–48&nbsp;hours to propagate).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Set these nameservers at your registrar (GoDaddy, Namecheap, …)
          </p>
          {(info?.nameservers ?? ["ns1.stackdns.com", "ns2.stackdns.com", "ns3.stackdns.com"]).map(ns => (
            <div key={ns} className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
              <code className="text-sm">{ns}</code>
              <Button size="sm" variant="ghost" onClick={() => copy(ns)}>Copy</Button>
            </div>
          ))}
          <p className="text-xs text-muted-foreground">
            In your registrar's dashboard look for "Nameservers" → "Use custom nameservers",
            replace the existing entries with the three above, and save. Everything else
            (website, mailboxes, DNS, SSL) is then configured automatically by us.
          </p>
        </div>

        <div>
          <button
            type="button"
            className="text-xs font-medium text-primary hover:underline"
            onClick={() => setShowAdvanced(v => !v)}
          >
            {showAdvanced ? "Hide" : "Advanced:"} keep your current DNS (email-only setup)
          </button>
          {showAdvanced && (
            <div className="mt-2 space-y-2">
              <p className="text-xs text-muted-foreground">
                If you want to keep DNS at your current provider (e.g. your website stays
                elsewhere), add these records instead. Note: with this setup we can't manage
                your DNS automatically.
              </p>
              {(info?.email_records ?? []).map((r, i) => (
                <div key={i} className="rounded-lg border border-border px-3 py-2 text-xs">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono">
                      {r.type} {r.host} → {r.value}{r.priority != null ? ` (priority ${r.priority})` : ""}
                    </span>
                    <Button size="sm" variant="ghost" onClick={() => copy(r.value)}>Copy</Button>
                  </div>
                  <p className="mt-1 text-muted-foreground">{r.note}</p>
                </div>
              ))}
              {info?.dkim_note && <p className="text-xs text-muted-foreground">{info.dkim_note}</p>}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/** Shown while a transfer is pending or after it failed — lets the customer
 *  resubmit a corrected EPP/auth code themselves (retry is free; they paid). */
function TransferBanner({ container }: { container: ContainerDetailDto }) {
  const status = (container.transfer_status ?? "").toLowerCase();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [epp, setEpp] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  if (status !== "pending" && status !== "failed") return null;

  async function resubmit() {
    setBusy(true);
    try {
      await api.post(`/api/containers/${container.id}/retry-transfer`, { epp_code: epp.trim() });
      toast({ title: "Transfer resubmitted", description: "We've sent the new code to the registrar.", variant: "success" });
      setEpp("");
      qc.invalidateQueries({ queryKey: ["containers", container.id] });
    } catch (err) {
      toast({
        title: "Couldn't resubmit",
        description: err instanceof ApiError ? err.message : "Please try again.",
        variant: "destructive"
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className={status === "failed" ? "border-destructive/40" : "border-amber-300/60"}>
      <CardContent className="space-y-3 py-4">
        <div className="flex items-start gap-3">
          <RefreshCw className={`mt-0.5 h-4 w-4 shrink-0 ${status === "failed" ? "text-destructive" : "text-amber-500"}`} />
          <div className="text-sm">
            {status === "failed" ? (
              <>
                <p className="font-medium">The transfer of {container.domain_name} couldn't start.</p>
                <p className="mt-1 text-muted-foreground">
                  This usually means the EPP/auth code was incorrect or expired, or the domain is locked
                  at your current registrar. Get a fresh code, make sure the domain is unlocked, and
                  resubmit below — <strong>no extra charge</strong>.
                </p>
              </>
            ) : (
              <>
                <p className="font-medium">Transfer of {container.domain_name} is in progress.</p>
                <p className="mt-1 text-muted-foreground">
                  Transfers typically complete within 5–7 days (your current registrar may email you to
                  approve it). If you were told the auth code was wrong, you can resubmit a new one below.
                </p>
              </>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Input
            placeholder="New EPP / auth code"
            value={epp}
            onChange={(e) => setEpp(e.target.value)}
            className="sm:max-w-xs"
          />
          <Button variant="outline" disabled={busy || epp.trim().length === 0} onClick={resubmit}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Resubmit transfer
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function BackLink() {
  return (
    <Link
      to="/domains"
      className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
    >
      <ArrowLeft className="h-4 w-4" />
      All domains
    </Link>
  );
}

function DomainHeader({ container }: { container: ContainerDetailDto }) {
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-accentSoft text-primary">
            <Globe className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h1 className="truncate font-display text-2xl font-bold tracking-tight sm:text-3xl">
              {container.domain_name}
            </h1>
            <p className="mt-0.5 text-sm text-muted-foreground">
              {container.registrar ?? container.registrar_provider}
              {container.renewal_date && ` · renews ${formatDate(container.renewal_date)}`}
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge status={container.status} />
        {container.twentyi_package_id ? (
          <Badge variant="soft">20i Provisioned</Badge>
        ) : (
          <Badge variant="outline">Not provisioned</Badge>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const variant: React.ComponentProps<typeof Badge>["variant"] =
    status === "active" ? "success" : "outline";
  return (
    <Badge variant={variant} className={cn("uppercase", status !== "active" && "text-muted-foreground")}>
      {status}
    </Badge>
  );
}

function UsageGrid({ container }: { container: ContainerDetailDto }) {
  const a = container.allocation;
  const u = container.usage;
  if (!a || !u) return null;

  const metrics = [
    { label: "Mailboxes",     used: u.mailboxes_used,   allocated: a.mailboxes_allocated },
    { label: "Storage (GB)",  used: u.storage_gb_used,  allocated: a.storage_gb_allocated },
    { label: "Bulk emails",   used: u.bulk_emails_used, allocated: a.bulk_emails_allocated },
    { label: "Websites",      used: u.websites_used,    allocated: a.websites_allocated },
    { label: "SSL certs",     used: u.ssl_certs_used,   allocated: a.ssl_certs_allocated },
    { label: "Aliases",       used: u.aliases_used,     allocated: a.aliases_allocated }
  ];

  // Auto mode with nothing reserved = this domain draws from the plan's
  // shared pool. Showing "0 / 0" everywhere reads like a bug, so label the
  // denominator honestly and point at the Allocation tab instead.
  const sharedPool =
    a.allocation_mode?.toLowerCase() === "auto" &&
    metrics.every(m => m.allocated === 0);

  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle>Usage</CardTitle>
        <CardDescription>
          {sharedPool ? (
            <>
              This domain draws from your plan's shared pool — no per-domain
              limits reserved. Set them on the Allocation tab if you want caps.
            </>
          ) : (
            <>Used out of allocated for this domain. Last sync {formatDate(u.last_synced_at ?? undefined)}.</>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="grid grid-cols-2 gap-4 sm:grid-cols-3 sm:gap-6">
        {metrics.map(m => (
          <div key={m.label} className="min-w-0">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{m.label}</p>
            <p className="mt-1 font-display text-xl font-bold tracking-tight sm:text-2xl">
              {fmt(m.used)}
              <span className="text-base font-normal text-muted-foreground">
                {" "}/ {sharedPool ? "pool" : (fmt(m.allocated) || "—")}
              </span>
            </p>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function fmt(n: number) {
  if (n === 0) return "0";
  if (Math.abs(n) % 1 < 0.001) return n.toLocaleString();
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function ServicesList({ container }: { container: ContainerDetailDto }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Attached services</CardTitle>
        <CardDescription>
          What's running for {container.domain_name}.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {container.services.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nothing yet. Mailboxes you create on the Mailboxes tab will show up here.
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {container.services.map(s => (
              <li key={s.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                    <ServerCog className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-sm font-medium capitalize">{s.service_type.replace(/_/g, " ")}</p>
                    <p className="text-xs text-muted-foreground">
                      {s.provider}{s.provider_reference && ` · ${s.provider_reference}`}
                    </p>
                  </div>
                </div>
                <Badge variant={s.enabled ? "success" : "outline"} className="uppercase">
                  {s.status}
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
      {container.twentyi_package_id && (
        <div className="border-t border-border px-5 py-3 sm:px-6">
          <a
            href={`https://my.20i.com/packages/${container.twentyi_package_id}`}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Open in 20i Control Panel
          </a>
        </div>
      )}
    </Card>
  );
}

// ── States ────────────────────────────────────────────────────
function DetailSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-4 w-24" />
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between">
        <div className="flex items-center gap-3">
          <Skeleton className="h-10 w-10 rounded-lg" />
          <div className="space-y-2">
            <Skeleton className="h-7 w-48" />
            <Skeleton className="h-4 w-32" />
          </div>
        </div>
        <Skeleton className="h-6 w-24" />
      </div>
      <Skeleton className="h-10 w-72" />
      <Skeleton className="h-48 w-full" />
    </div>
  );
}

function NotFoundState() {
  return (
    <div className="space-y-4">
      <BackLink />
      <Card>
        <CardContent className="py-12 text-center text-sm text-muted-foreground">
          We couldn't find that domain on your account.
        </CardContent>
        <div className="border-t border-border px-5 py-3 text-center sm:px-6">
          <Button asChild variant="outline">
            <Link to="/domains">Back to all domains</Link>
          </Button>
        </div>
      </Card>
    </div>
  );
}
